import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { PermissionStore } from '@engineering11/access-web'
import { BehaviorSubject, catchError, filter, firstValueFrom, map, Observable, of, Subject, switchMap, take, timeout } from 'rxjs'
import { ITourActiveStep, ITourDefinition, ITourStep } from '../models/tour.model'

/**
 * Tour engine (Epic 4, story 4.4). A UI-only runtime that walks a declarative `ITourDefinition`:
 * for each step it navigates via the Angular `Router`, resolves the `[data-tour-id]` anchor, and
 * emits an `ITourActiveStep` the `TourOverlayComponent` renders (spotlight + narration + controls).
 *
 * design-contract.md → §Surface 3.
 *
 * CAP-3 (UI-only): this service injects ONLY `Router`, `PermissionStore` (reads), and `DOCUMENT`.
 * It imports/injects NO assistant service, repository, `RestApiClient`, or Firestore collaborator.
 * A full run (walk / skip / back / exit / complete) performs zero data mutations and zero
 * assistant/LLM calls — its only collaborators are `router.navigate`, permission reads, and DOM reads.
 * In story 4.4 skip/exit/complete ONLY tear down the overlay; the completion/mode endpoint is 4.7.
 *
 * CAP-2 (graceful skip): a step is unresolvable — and is skipped in the direction of travel with no
 * error — when its declared `requiredPermissions` are ungranted (checked side-effect-free, WITHOUT
 * navigating), when `router.navigate` resolves `false` / redirects (guard block), or when its anchor
 * is absent after a bounded render wait. If every remaining step is unresolvable the tour tears down.
 */
@Injectable({ providedIn: 'root' })
export class TourEngineService {
  /** Max anchor-resolution polls before a step is treated as anchor-absent (bounded — never hangs). */
  private static readonly MAX_ANCHOR_ATTEMPTS = 20
  /** Upper bound on waiting for permissions to load before a permission check resolves to false. */
  private static readonly PERMISSION_WAIT_MS = 5000

  private steps: ITourStep[] = []
  private index = -1
  /** Guards against overlapping resolution (rapid double-clicks on next/back): a run is in flight. */
  private resolving = false
  /**
   * Monotonic run token. Every `run()` and every `teardown()` bumps it; an in-flight `resolveFrom`
   * captures the token and aborts (never emits) once it is superseded — so exiting/skipping mid-
   * navigation can never resurrect the overlay on a stale step.
   */
  private runToken = 0

  /** The active step view-state, or `null` when no tour is running. */
  readonly activeStep$ = new BehaviorSubject<ITourActiveStep | null>(null)
  /** Derived: whether a tour is currently active. */
  readonly isActive$: Observable<boolean> = this.activeStep$.pipe(map(step => step !== null))

  /**
   * Terminal-resolution seam (story 4.6). Emits ONCE when an active tour tears down — `skipped` on
   * skip/exit, `completed` on a forward-overflow / all-unresolvable finish. It NEVER emits on
   * step-to-step navigation (next/back that land on a step) or on backward underflow. Keeping this an
   * event (rather than an injected endpoint) preserves the engine's UI-only walk: `AssistantPostureService`
   * subscribes and owns the completion endpoint call; the engine gains no HTTP/assistant collaborator.
   */
  readonly tourTerminated$ = new Subject<'completed' | 'skipped'>()

  constructor(private router: Router, private permissionStore: PermissionStore, @Inject(DOCUMENT) private document: Document) {}

  /** Begin a new tour from step 0, resolving forward. Resets any prior state. */
  startTour(definition: ITourDefinition): Promise<void> {
    this.steps = definition.steps ?? []
    this.index = -1
    return this.run(0, 1)
  }

  /** Advance to the next resolvable step (forward). */
  next(): Promise<void> {
    return this.run(this.index + 1, 1)
  }

  /** Return to the previous resolvable step (backward). */
  back(): Promise<void> {
    return this.run(this.index - 1, -1)
  }

  /** Skip the tour: tear down the overlay and end the tour, emitting a terminal `skipped` resolution. */
  skip(): void {
    this.teardown('skipped')
  }

  /** Exit the tour: tear down the overlay and end the tour, emitting a terminal `skipped` resolution. */
  exit(): void {
    this.teardown('skipped')
  }

  /**
   * Public entry for a resolution run. Ignores overlapping calls (a rapid second next/back while the
   * first is still navigating) and stamps a fresh run token so a later teardown can supersede it.
   */
  private async run(index: number, dir: 1 | -1): Promise<void> {
    if (this.resolving) return
    this.resolving = true
    const token = ++this.runToken
    try {
      await this.resolveFrom(index, dir, token)
    } finally {
      this.resolving = false
    }
  }

  /**
   * Clear all state and remove the overlay (`activeStep$ → null`); supersedes any in-flight run. Emits
   * `tourTerminated$.next(reason)` ONLY when a tour was actually active before clearing (an `activeStep$`
   * value or a non-empty `steps` list), so a no-op teardown (skip with no tour running) is silent. Called
   * only on terminal resolution — never on step-to-step navigation — so the walk stays mutation-free.
   */
  private teardown(reason: 'completed' | 'skipped'): void {
    const wasActive = this.activeStep$.value !== null || this.steps.length > 0
    this.runToken++
    this.steps = []
    this.index = -1
    this.activeStep$.next(null)
    if (wasActive) {
      this.tourTerminated$.next(reason)
    }
  }

  /**
   * Resolve the step at `index`, skipping in `dir` when it is unresolvable. Forward overflow completes
   * the tour (teardown); backward underflow is a no-op (stay on the current step, so Back on the first
   * step does not end the tour). Recursion is bounded by `steps.length` (index always moves by `dir`),
   * so an all-unresolvable run terminates cleanly with no infinite loop. Every await re-checks `token`
   * so a teardown or newer run mid-flight aborts before emitting a stale step.
   */
  private async resolveFrom(index: number, dir: 1 | -1, token: number): Promise<void> {
    if (token !== this.runToken) return
    if (index < 0) return // backward underflow — keep the current step
    if (index >= this.steps.length) {
      this.teardown('completed') // forward overflow / all-unresolvable — tour complete
      return
    }
    const step = this.steps[index]

    // Permission gate — an ungranted step is skipped WITHOUT navigating (keeps the run UI-only).
    if (step.requiredPermissions && step.requiredPermissions.length > 0) {
      const permitted = await this.isPermitted(step.requiredPermissions)
      if (token !== this.runToken) return
      if (!permitted) {
        await this.resolveFrom(index + dir, dir, token)
        return
      }
    }

    // Navigate via the router, then judge the step by the URL we actually land on — NOT the navigate
    // boolean. A same-URL navigation (a step whose route is the page we're already on, e.g. successive
    // nav-anchored steps that all sit on /jobs/list) is ignored by the router and resolves `false` even
    // though we are exactly where the step wants to be; skipping on that boolean silently drops every
    // such step. A guard block or redirect lands on a different URL and is still caught by the URL check.
    const commands = Array.isArray(step.route) ? step.route : [step.route]
    try {
      await this.router.navigate(commands)
    } catch {
      // A thrown navigation is inaccessible; the URL check below skips it.
    }
    if (token !== this.runToken) return
    if (!this.urlMatchesTarget(step.route)) {
      await this.resolveFrom(index + dir, dir, token)
      return
    }

    // Resolve the anchor after a bounded render wait; absent = skip.
    const el = await this.resolveAnchor(step.anchor)
    if (token !== this.runToken) return
    if (!el) {
      await this.resolveFrom(index + dir, dir, token)
      return
    }

    // Bring the anchor into view before measuring, so the spotlight lands on-screen.
    el.scrollIntoView({ block: 'center', inline: 'center' })
    const domRect = el.getBoundingClientRect()
    // An anchor present in the DOM but with no laid-out box (e.g. display:none) yields a zero rect —
    // treat it as unresolvable rather than drawing a zero-size spotlight.
    if (domRect.width === 0 && domRect.height === 0) {
      await this.resolveFrom(index + dir, dir, token)
      return
    }

    this.index = index
    this.activeStep$.next({
      step,
      index,
      total: this.steps.length,
      rect: { top: domRect.top, left: domRect.left, width: domRect.width, height: domRect.height },
    })
  }

  /**
   * Side-effect-free permission check: wait for `PermissionStore.loaded$` then read
   * `hasAllPermissions`. No navigation is performed here (mirrors how routes gate with a permission
   * guard). Bounded and error-safe — if the store never loads (auth expiry), the streams error, or
   * they complete without emitting, the step resolves as not-permitted (→ skipped) rather than hanging.
   */
  private async isPermitted(permissions: string[]): Promise<boolean> {
    return firstValueFrom(
      this.permissionStore.loaded$.pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.permissionStore.hasAllPermissions(permissions)),
        take(1),
        timeout(TourEngineService.PERMISSION_WAIT_MS),
        catchError(() => of(false))
      ),
      { defaultValue: false }
    )
  }

  /**
   * True when the current router URL matches the step's target route (i.e. no guard redirect).
   * Public so story 4.9's `navigate` client tool reuses this normalization for guard-redirect
   * detection (a guard that redirects makes `router.navigate` resolve true but lands elsewhere).
   */
  public urlMatchesTarget(route: string | any[]): boolean {
    return this.normalizeUrl(this.routeToUrl(route)) === this.normalizeUrl(this.router.url)
  }

  private routeToUrl(route: string | any[]): string {
    if (Array.isArray(route)) {
      return (
        '/' +
        route
          .map(seg => String(seg).replace(/^\/+|\/+$/g, ''))
          .filter(Boolean)
          .join('/')
      )
    }
    return route
  }

  private normalizeUrl(url: string): string {
    const path = ('/' + (url ?? ''))
      .replace(/\/{2,}/g, '/')
      .replace(/[?#].*$/, '')
      .replace(/\/+$/, '')
    return path === '' ? '/' : path
  }

  /**
   * Poll `document.querySelector('[data-tour-id="…"]')` a bounded number of times so a not-yet-rendered
   * anchor gets a brief chance to appear, then resolves `null` (→ the step is skipped). Never hangs.
   */
  private resolveAnchor(anchor: string): Promise<HTMLElement | null> {
    const selector = `[data-tour-id="${anchor}"]`
    return new Promise<HTMLElement | null>(resolve => {
      let attempts = 0
      const tick = () => {
        const el = this.document.querySelector(selector) as HTMLElement | null
        if (el) {
          resolve(el)
          return
        }
        if (++attempts >= TourEngineService.MAX_ANCHOR_ATTEMPTS) {
          resolve(null)
          return
        }
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(tick)
        } else {
          setTimeout(tick, 16)
        }
      }
      tick()
    })
  }
}
